import {
  AuthLocalStorage,
  getProfilesFromSessionStorage,
  getToken,
  Profile,
} from 'application/sessionStorage/auth';
import axios from 'axios';
import { usersRefreshToken } from 'application/store/reducers/Users/ActionCreators';
import dayjs from 'dayjs';
import { store } from 'application/store';
import { ApplicationType, x_app_type } from '../../application';

/**
 * Creates an instance of Axios with the baseURL set to the REACT_APP_BASE_URL environment variable
 * and responseType set to 'blob'.
 */
const axiosBlobInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  responseType: 'blob',
});

// Add a request interceptor
axiosBlobInstance.interceptors.request.use(
  async function (config) {
    const profile: Profile[] = getProfilesFromSessionStorage()!;

    // Gets the token expiration date from the local storage and subtracts 59 minutes from it.
    const date = localStorage.getItem(AuthLocalStorage.accessTokenExpiresAt);
    const minusMinuteDate = date
      ? dayjs(date).subtract(59, 'minute').toISOString()
      : null;

    // Checks if the token is still valid based on the expiration date.
    const isDateValid =
      dayjs().isValid() && dayjs(minusMinuteDate).diff(dayjs(), 'minute') <= 59;

    // Checks if the token has expired based on the expiration date.
    const isExpiredToken = minusMinuteDate
      ? dayjs().isAfter(minusMinuteDate)
      : false;

    // If the token is invalid or expired, refreshes it using the refreshToken and email from the profile.
    if (!isDateValid || isExpiredToken) {
      if (!profile?.length) return;

      const data = await store.dispatch(
        usersRefreshToken({
          refreshToken: profile[0].refreshToken,
          email: profile[0].email,
        }),
      );

      // If the token refresh was successful, sets the new token in the headers.
      if (data && data.meta.requestStatus === 'fulfilled') {
        return {
          ...config,
          ...{
            headers: {
              'Access-Control-Allow-Origin': '*',
              'Content-type': 'Application/json',
              Authorization: `Bearer ${getToken()}`,
              [x_app_type]: ApplicationType.ProfessionalWeb,
            },
          },
        };
      }
    }

    // Gets the token from the local storage.
    const token = getToken();
    if (!token) return;

    return {
      ...config,
      ...(token
        ? {
            headers: {
              'Access-Control-Allow-Origin': '*',
              'Content-type': 'Application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        : {}),
    };
  },
  function (error) {
    // Do something with request error
    return Promise.reject(error);
  },
);

// Add a response interceptor
axiosBlobInstance.interceptors.response.use(
  function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    return response;
  },
  function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    return Promise.reject(error);
  },
);

export default axiosBlobInstance;
